import _ from 'lodash';
import React, { useMemo, useState, useCallback, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

import { useReportExplore } from '../../redux';
import Chip, { ChipRow } from 'widgets-v5/chip';
import { DialogTitle } from 'widgets-v6/modal';
import { DimensionsSelector } from 'widgets-v6/dimension-selector';

import { getAvailableSnowflakeCategories } from 'pages/reports/editor/constants';

function buildDimensionCategories(options) {
    const opts = options || [];
    const categoriesSnowFlake = getAvailableSnowflakeCategories();
    categoriesSnowFlake.dimension = [
        'date',
        'hour',
        'day_of_week',
        'week_by_campaign_start_day',
        'week_by_monday_to_sunday',
        'month',
        'quarter',
    ];

    // quick lookup: which category a dimension value belongs to
    const categoryByDimensionValue = {};
    _.forEach(categoriesSnowFlake, (cat, catId) => {
        _.forEach(cat.dimensions || [], dimValue => {
            categoryByDimensionValue[dimValue] = catId;
        });
    });

    // Build a lookup for top-level options by value (helps dedupe)
    const topLevelByValue = _.keyBy(opts, 'value');

    // 1) Expand each option into a list of "dimension candidates"
    // - if has children: use children as leaf dimensions
    // - else: use itself
    const expanded = _.flatMap(opts, opt => {
        if (_.size(opt.children) > 0) {
            return _.map(opt.children, child => ({
                label: child.label,
                value: child.value,
                // carry block flag from parent if child doesn't have it
                isBlocked: !!(child.invalidBecauseOfDate ?? opt.invalidBecauseOfDate),
                // keep metadata if you need later
                category: child.category,
                description: child.description,
            }));
        }

        return [
            {
                label: opt.label,
                value: opt.value,
                isBlocked: !!opt.invalidBecauseOfDate,
                category: opt.category,
                description: opt.description,
            },
        ];
    });

    // 2) Dedupe by value.
    // Prefer top-level option label if it exists; otherwise keep flattened child.
    const deduped = _(expanded)
        .groupBy('value')
        .map((items, value) => {
            const top = topLevelByValue[value];
            if (top) {
                return {
                    label: top.label,
                    value: top.value,
                    isBlocked: !!top.invalidBecauseOfDate,
                    category: top.category,
                    description: top.description,
                };
            }
            // fall back to first flattened child
            return items[0];
        })
        .value();

    // 3) Assign category per dimension value (or custom)
    const grouped = _.groupBy(deduped, dim => {
        return categoryByDimensionValue[dim.value] || 'custom';
    });

    // 4) Build categories in the same order as categoriesSnowFlake, plus "custom"
    const orderedCategoryIds = [...Object.keys(categoriesSnowFlake), 'custom'];

    return _.map(orderedCategoryIds, categoryId => {
        const label =
            categoryId === 'custom'
                ? 'Custom Dimension'
                : _.get(categoriesSnowFlake, [categoryId, 'label'], _.startCase(categoryId));

        const catDimensions = grouped[categoryId] || [];

        return {
            label,
            value: categoryId,
            dimensions: _.map(catDimensions, d => ({
                label: d.label,
                value: d.value,
                isBlocked: !!d.isBlocked,
                attributes: [],
            })),
        };
    }).filter(cat => _.size(cat.dimensions) > 0);
}

function buildDimensionsMapping(dimensionCategories) {
    // mapping used by the Selected panel in DimensionsSelector :contentReference[oaicite:4]{index=4}
    const mapping = {};
    const dims = _(dimensionCategories)
        .map('dimensions')
        .flatten()
        .value();

    _.forEach(dims, d => {
        mapping[d.value] = {
            label: d.label,
            attributes: _.reduce(
                d.attributes || [],
                (acc, a) => {
                    acc[a.value] = a.label;
                    return acc;
                },
                {}
            ),
        };
    });

    return mapping;
}

function DimensionSelectorContainer() {
    const {
        allDimensionOptions,
        hasDimensionsAvailable,
        // selectableDimensionOptions,
        selectedDimensions,
        // addDimension,
        updateDimensions,
    } = useReportExplore();

    const dimensionCategories = useMemo(() => buildDimensionCategories(allDimensionOptions), [
        allDimensionOptions,
    ]);

    const dimensionsMapping = useMemo(() => buildDimensionsMapping(dimensionCategories), [
        dimensionCategories,
    ]);

    const selectedDimensionsLabels = useMemo(() => {
        const allDimensionOptionsMap = new Map();
        _.forEach(allDimensionOptions, item => allDimensionOptionsMap.set(item.value, item));
        return _.map(selectedDimensions, dimension => {
            const found = allDimensionOptionsMap.get(dimension);
            return _.get(found, 'label', dimension);
        });
    }, [allDimensionOptions, selectedDimensions]);

    return (
        <Grid container alignItems="center">
            {selectedDimensions.length > 0 && (
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <Typography variant="body1">{'Split on:'}</Typography>
                    <ChipRow>
                        {_.map(selectedDimensionsLabels, (label, index) => (
                            <React.Fragment>
                                <Chip label={label} />
                                {index < selectedDimensionsLabels.length - 1 && (
                                    <SubdirectoryArrowRightIcon />
                                )}
                            </React.Fragment>
                        ))}
                    </ChipRow>
                </Stack>
            )}
            {hasDimensionsAvailable && (
                <ManageSplitButton
                    allDimensionOptions={allDimensionOptions}
                    selectedOptions={selectedDimensions}
                    updateDimensions={updateDimensions}
                    dimensionCategories={dimensionCategories}
                    dimensionsMapping={dimensionsMapping}
                />
            )}
        </Grid>
    );
}

function ManageSplitButton({
    allDimensionOptions,
    selectedOptions,
    updateDimensions,
    dimensionCategories,
    dimensionsMapping,
}) {
    const [open, setOpen] = useState(false);

    const [dimensionsValues, setDimensionsValues] = useState(selectedOptions || []);

    // keep local state in sync if store changes (e.g. external reset)
    useEffect(() => {
        setDimensionsValues(selectedOptions || []);
    }, [selectedOptions]);

    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);

    const handleApply = useCallback(() => {
        updateDimensions(dimensionsValues);
        handleClose();
    }, [updateDimensions, dimensionsValues, handleClose]);

    return (
        <>
            <Button onClick={handleOpen}>Manage Split</Button>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                aria-labelledby="dimension-selector-dialog"
            >
                <DialogTitle onClose={handleClose}>Manage Split</DialogTitle>

                <Box p={2}>
                    <DimensionsSelector
                        dimensionCategories={dimensionCategories}
                        dimensionsMapping={dimensionsMapping}
                        dimensionsValues={dimensionsValues}
                        dimensionsOnChange={setDimensionsValues}
                        errors={{}}
                        showErrors={false}
                        ownOrganization={undefined}
                        chipItems={allDimensionOptions}
                        chipValue={dimensionsValues}
                        chipOnChange={setDimensionsValues}
                        chipLabel="Split on:"
                        showChips
                    />
                </Box>

                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>

                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disabled={!dimensionsValues || dimensionsValues.length === 0}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default DimensionSelectorContainer;
